import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Autocomplete } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addCategoryDataExceptionRobot, addCategoryDataRobot, changeLongContextExceptionRobot, changeLongContextRobot } from "@/store/ses/sesRobotSlice";
import {
	addCategoryDataElem,
	addCategoryDataEndpoint,
	addCategoryDataEndpointEntry,
	addCategoryDataException,
	changeLongContextDataElem,
	changeLongContextEndpoint,
	changeLongContextEndpointEntry,
	changeLongContextException,
} from "@/store/ses/sesItemConfigSlice";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import { selectCategoriesList } from "@/store/qas/qasCategorySlice";
import { selectSesHints } from "@/store/hintsSlice";
import { useTranslate } from "@/hooks";
import { createObjectWithCondition } from "@/helpers";
import { colorSecondaryLight3 } from "@/constants/colors";
import { AtomicCondition, IConditionData, IConditionVariable } from "@/types/ses/sesItemConfigTypes";
import { ICategory } from "@/types/qas/qasCategoryTypes";
import { IDataElemItem } from "@/types/ses/sesDataElemTypes";
import { CustomButton, NoOptionText, RenderOption } from "@/ui";
import { CustomCheckbox, CustomTextField, DraggablePopoverComponent } from "@/ui";
import ConditionBlock from "@/components/ConditionBlock/ConditionBlock";
import FormAddingCondition from "@/components/Forms/FormAddingCondition/FormAddingCondition";
import { ICategoryConditionsProps } from "./CategoryConditions.props";
import styles from "./CategoryConditions.module.scss";

const CategoryConditions = ({
	action,
	idx,
	channel,
	changeFlgHandler,
	actionFor,
	isDisabled,
	anchorElCategoriesSettings,
	setAnchorElCategoriesSettings,
	isFilterActive,
	setNumberOfNonExistentCategories,
}: ICategoryConditionsProps): JSX.Element => {
	const [inputCategory, setInputCategory] = useState<ICategory | null>(null); // категория
	const [checkLongContext, setCheckLongContext] = useState<boolean>(inputCategory && action.longContext?.includes(inputCategory?.id) ? true : false); // флаг запроса в QAS с учетом прошлого контекста

	const dispatch = useAppDispatch();
	const categoriesList = useAppSelector(selectCategoriesList); // store - список категорий
	const dataElemList = useAppSelector(selectDataList); // список ЭД
	const sesHints = useAppSelector(selectSesHints);

	const dataElemListOptions = useMemo<{ dataElemSlaveData: IDataElemItem[]; dataElem: IDataElemItem[] } | null>(() => {
		return dataElemList.data
			? {
					dataElemSlaveData: dataElemList.data.filter((dataElem) => dataElem.type !== "container"),
					dataElem: dataElemList.data,
			  }
			: null;
	}, [JSON.stringify(dataElemList.data)]);

	const translate = useTranslate(); // hook для перевода текста

	useEffect(() => {
		if (Object.keys(action.categories || {}).length > 0) {
			if (!inputCategory) {
				const firstCategoryOnTheList = Object.keys(action.categories || {})[0];
				const categoryFound = categoriesList.data.find((categoryItem) => categoryItem.id === firstCategoryOnTheList);
				setInputCategory(categoryFound || { id: firstCategoryOnTheList, name: `${firstCategoryOnTheList} (${translate("title_notFound")})` });
			}
			let numberOfNonExistentCategories: number = 0; // кол-во несуществующих категорий
			const categoryIds = Object.keys(action.categories);
			categoryIds.forEach((categoryId) => {
				if (categoryId in categoriesList.dictionary) return;
				else numberOfNonExistentCategories += 1;
			});
			setNumberOfNonExistentCategories(numberOfNonExistentCategories); // пишем в state кол-во несуществующих категорий
		} else {
			setNumberOfNonExistentCategories(0); // обнуляем кол-во несуществующих категорий
		}
	}, []);

	// следим за выбранной категорией и обновляем флаг запроса в QAS с учетом прошлого контекста
	useEffect(() => {
		setCheckLongContext(
			action.action === "say" && action.type === "internal" && action.service === "qas-ask" && inputCategory && action.longContext?.includes(inputCategory.id)
				? true
				: false
		);
	}, [inputCategory]);

	// обработчик изменения флага запроса qas с учетом прошлого контекста
	const changeLongContextHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.checked;
		setCheckLongContext(value);
		const data = { channel, channelIdx: idx, categoryId: inputCategory?.id || "", flag: value };
		if (actionFor.for === "robotException") {
			dispatch(changeLongContextExceptionRobot({ exception: actionFor.exception, ...data })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(changeLongContextException({ targetObject: "dataElement", exception: actionFor.exception, ...data })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(changeLongContextException({ targetObject: "endpoint", exception: actionFor.exception, ...data })); // исключения КТ
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(changeLongContextEndpointEntry({ ...data })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(changeLongContextRobot({ actionEvent: actionFor.event, ...data })); // события робота
			actionFor.for === "dataElement" && dispatch(changeLongContextDataElem({ actionEvent: actionFor.event, ...data })); // ЭД
			actionFor.for === "endpoint" && dispatch(changeLongContextEndpoint({ ...data })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	// обработчик добавления данных категории
	const addCategoryDataHandler = (categoryBlockIdx: number, data?: AtomicCondition | null, orBlockIndex?: number): void => {
		const conditionData = (data as IConditionData | IConditionVariable) || createObjectWithCondition("data");
		const payloadData = { channel, channelIdx: idx, categoryId: inputCategory?.id || "", categoryBlockIdx, data: conditionData };
		if (actionFor.for === "robotException") {
			dispatch(addCategoryDataExceptionRobot({ exception: actionFor.exception, orBlockIndex, ...payloadData })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(addCategoryDataException({ targetObject: "dataElement", exception: actionFor.exception, orBlockIndex, ...payloadData })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(addCategoryDataException({ targetObject: "endpoint", exception: actionFor.exception, orBlockIndex, ...payloadData })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(addCategoryDataEndpointEntry({ orBlockIndex, ...payloadData })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(addCategoryDataRobot({ actionEvent: actionFor.event, orBlockIndex, ...payloadData })); // события робота
			actionFor.for === "dataElement" && dispatch(addCategoryDataElem({ actionEvent: actionFor.event, orBlockIndex, ...payloadData })); // ЭД
			actionFor.for === "endpoint" && dispatch(addCategoryDataEndpoint({ orBlockIndex, ...payloadData })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	return (
		<DraggablePopoverComponent
			anchorEl={anchorElCategoriesSettings}
			setAnchorEl={setAnchorElCategoriesSettings}
			header="title_conditionsCategories"
			additionalButtons={
				<>
					{/* сохранить */}
					{actionFor.isShownSaveButton && actionFor.isAccessSaveButton && !isDisabled && (
						<CustomButton fullWidth disabled={actionFor.disabledSaveButton} onClick={actionFor.onClickSaveButtonHandler}>
							{translate("button_save")}
						</CustomButton>
					)}
				</>
			}
			sx={{ minWidth: "400px" }}
		>
			{/* категория */}
			<Autocomplete
				fullWidth
				autoHighlight
				options={[
					// на случай потеряшек
					...Object.keys(action?.categories || {})
						.filter((categoryId) => !(categoryId in categoriesList.dictionary))
						.map((categoryId) => ({ id: categoryId, name: `${categoryId} (${translate("title_notFound")})` })),
					...categoriesList.data,
				]}
				value={inputCategory}
				onChange={(_, value) => setInputCategory(value)}
				noOptionsText={<NoOptionText />}
				renderInput={(params) => <CustomTextField {...params} hint={"hint_actionCategory"} label={translate("select_category")} />}
				sx={{ backgroundColor: colorSecondaryLight3, marginBlock: "8px" }}
				getOptionLabel={(option) => option.name}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option.id}>
							{/* со счетчиком */}
							{option.name} {action.categories?.[option.id]?.length > 0 && `(${action.categories[option.id].length})`}
						</RenderOption>
					);
				}}
			/>
			{inputCategory && (
				<>
					{/* пусто */}
					{isDisabled && !action.categories?.[inputCategory?.id || ""] && <div className={styles.empty}>{translate("title_empty")}</div>}
					{/* запрос в qas с учетом прошлого контекста */}
					{action.categories?.[inputCategory?.id || ""]?.length > 0 && (
						<CustomCheckbox
							checked={checkLongContext}
							disabled={isDisabled}
							onChange={changeLongContextHandler}
							label="checkbox_queryInQasTakingIntoAccountPastContext"
							hint={sesHints.hint_sesConfigureQasContextSearch}
							hintPosition="left"
							sxFormControlLabel={{ mb: "8px" }}
						/>
					)}
					{/* блоки условий присвоения категорий */}
					{action.categories?.[inputCategory?.id || ""]?.map((categoryDataBlock, categoryBlockIdx) => (
						// блоки ИЛИ
						<ConditionBlock
							conditionDataBlock={categoryDataBlock}
							conditionBlockIdx={categoryBlockIdx}
							isDisable={isDisabled}
							changeFlgHandler={changeFlgHandler}
							conditionFor={{
								for: "categoryAction",
								itsAction: actionFor.for,
								actionId: action.actionId,
								actionEvent:
									actionFor.for === "robotEvent" || actionFor.for === "dataElement"
										? actionFor.event
										: actionFor.for === "robotException" || actionFor.for === "dataException" || actionFor.for === "endpointException"
										? actionFor.exception
										: undefined,
								channel,
								channelIdx: idx,
								categoryId: inputCategory?.id || "",
							}}
							addConditionRunHandler={addCategoryDataHandler}
							key={categoryDataBlock.toString() + categoryBlockIdx + inputCategory?.id || ""}
							showAddCondtionBlockButton={false}
							dataElemListOptions={dataElemListOptions}
						/>
					))}
					{/* форма добавления условия */}
					{!isDisabled && !isFilterActive && (
						<FormAddingCondition
							conditionForFormVariant={!action.categories?.[inputCategory?.id || ""] || action.categories?.[inputCategory?.id || ""]?.length === 0}
							addCondition={(data) => addCategoryDataHandler(action.categories?.[inputCategory?.id || ""]?.length || 0, data)}
							listOfSkipConditions={["interval", "confirmation"]}
						/>
					)}
				</>
			)}
		</DraggablePopoverComponent>
	);
};

export default CategoryConditions;
